// Predefined L1 guardrail library (UI-only).
//
// These are ready-made regex guardrails an admin can add from the Guardrails
// page without authoring a pattern by hand. Only `name`, `pattern`, `action`,
// `message` and `ignore_case` are persisted (via the existing POST
// /api/admin/guardrails) — the rest (`description`, `risk`, `compliance`,
// `recommended`) drive the picker UI only and are never sent to the backend.
//
// Conventions, to stay aligned with utils/guardrails.py:
//   • action is always "reject" — `pass` is an allowlist short-circuit and is
//     intentionally not used in this curated library.
//   • patterns avoid inline (?i) flags; case handling is the per-preset
//     `ignore_case` boolean so structural IDs stay case-sensitive (low false
//     positives) while keyword rules match any casing.
//   • saved names are prefixed with their category tag (e.g. "[PII] India PAN")
//     so the guardrails table stays scannable.
//
// `risk` = false-positive likelihood in real dev traffic (drives the picker dot
// and which presets the "recommended starter pack" turns on).

export const GUARDRAIL_CATEGORIES = [
  { key: 'pii', label: 'PII / Identity', tag: 'PII' },
  { key: 'phi', label: 'Health data (PHI)', tag: 'PHI' },
  { key: 'financial', label: 'Financial identifiers', tag: 'FIN' },
  { key: 'secrets', label: 'Secrets / credentials', tag: 'SECRET' },
  { key: 'injection', label: 'Prompt injection / jailbreak', tag: 'SEC' },
  { key: 'exfil', label: 'Data exfiltration & SSRF', tag: 'EXFIL' },
  { key: 'compliance', label: 'Regulatory / market conduct', tag: 'REG' },
  { key: 'safety', label: 'Content safety', tag: 'SAFETY' },
];

const CATEGORY_TAG = Object.fromEntries(GUARDRAIL_CATEGORIES.map((c) => [c.key, c.tag]));

// Raw catalog. `id` is a stable slug (used for de-dupe keys in the picker).
const RAW_PRESETS = [
  // ── A — PII / Identity ──────────────────────────────────────────────────
  {
    id: 'pii-india-pan', category: 'pii', name: 'India PAN',
    description: 'Permanent Account Number (India tax ID), e.g. ABCDE1234F.',
    pattern: '\\b[A-Z]{5}[0-9]{4}[A-Z]\\b', ignore_case: false,
    risk: 'low', compliance: 'DPDP', recommended: true,
    message: 'Blocked: request appears to contain a PAN (India tax ID). Remove personal identifiers before sending.',
  },
  {
    id: 'pii-india-aadhaar', category: 'pii', name: 'India Aadhaar',
    description: '12-digit Aadhaar number (optionally space-grouped 4-4-4).',
    pattern: '\\b[2-9]\\d{3}\\s?\\d{4}\\s?\\d{4}\\b', ignore_case: false,
    risk: 'med', compliance: 'DPDP/UIDAI', recommended: true,
    message: 'Blocked: request appears to contain an Aadhaar number. Aadhaar must not be shared with the model.',
  },
  {
    id: 'pii-us-ssn', category: 'pii', name: 'US SSN',
    description: 'US Social Security Number, e.g. 123-45-6789.',
    pattern: '\\b\\d{3}-\\d{2}-\\d{4}\\b', ignore_case: false,
    risk: 'low', compliance: 'GDPR/SOC2', recommended: true,
    message: 'Blocked: request appears to contain a US Social Security Number.',
  },
  {
    id: 'pii-email', category: 'pii', name: 'Email address',
    description: 'Any email address. High false-positive risk in code/dev traffic.',
    pattern: '\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}\\b', ignore_case: true,
    risk: 'high', compliance: 'DPDP/GDPR', recommended: false,
    message: 'Blocked: request appears to contain an email address.',
  },
  {
    id: 'pii-india-mobile', category: 'pii', name: 'India mobile (+91)',
    description: '10-digit Indian mobile number, optional +91 prefix.',
    pattern: '\\b(?:\\+?91[\\-\\s]?)?[6-9]\\d{9}\\b', ignore_case: false,
    risk: 'med', compliance: 'DPDP', recommended: false,
    message: 'Blocked: request appears to contain a phone number.',
  },
  {
    id: 'pii-india-passport', category: 'pii', name: 'India passport',
    description: 'Indian passport number, e.g. A1234567.',
    pattern: '\\b[A-PR-WYa-pr-wy][1-9]\\d\\s?\\d{4}[1-9]\\b', ignore_case: false,
    risk: 'low', compliance: 'DPDP', recommended: true,
    message: 'Blocked: request appears to contain a passport number.',
  },
  {
    id: 'pii-voter-epic', category: 'pii', name: 'Voter ID (EPIC)',
    description: 'Indian Voter ID / EPIC number, e.g. ABC1234567.',
    pattern: '\\b[A-Z]{3}\\d{7}\\b', ignore_case: false,
    risk: 'med', compliance: 'DPDP', recommended: false,
    message: 'Blocked: request appears to contain a Voter ID (EPIC) number.',
  },
  {
    id: 'pii-dob-labeled', category: 'pii', name: 'Date of birth (labeled)',
    description: 'A date preceded by a "DOB" / "date of birth" label.',
    pattern: '\\b(?:dob|date of birth)\\b\\s*[:\\-]?\\s*\\d{1,4}[\\/\\-.]\\d{1,2}[\\/\\-.]\\d{1,4}', ignore_case: true,
    risk: 'med', compliance: 'DPDP', recommended: false,
    message: 'Blocked: request appears to contain a date of birth.',
  },

  // ── B — Financial identifiers ───────────────────────────────────────────
  {
    id: 'fin-card-pan', category: 'financial', name: 'Credit/debit card (PAN)',
    description: 'Visa / Mastercard / Amex / Discover primary account numbers.',
    pattern: '\\b(?:4\\d{12}(?:\\d{3})?|5[1-5]\\d{14}|3[47]\\d{13}|6(?:011|5\\d{2})\\d{12})\\b', ignore_case: false,
    risk: 'low', compliance: 'PCI-DSS', recommended: true,
    message: 'Blocked: request appears to contain a payment card number (PCI-DSS).',
  },
  {
    id: 'fin-cvv', category: 'financial', name: 'CVV (labeled)',
    description: 'A 3-4 digit value preceded by a CVV / CVC label.',
    pattern: '\\b(?:cvv|cvc|cvv2)\\b\\s*[:#]?\\s*\\d{3,4}\\b', ignore_case: true,
    risk: 'low', compliance: 'PCI-DSS', recommended: true,
    message: 'Blocked: request appears to contain a card CVV/CVC (PCI-DSS).',
  },
  {
    id: 'fin-ifsc', category: 'financial', name: 'India IFSC code',
    description: 'Bank branch IFSC code, e.g. HDFC0001234.',
    pattern: '\\b[A-Z]{4}0[A-Z0-9]{6}\\b', ignore_case: false,
    risk: 'low', compliance: 'RBI', recommended: true,
    message: 'Blocked: request appears to contain a bank IFSC code.',
  },
  {
    id: 'fin-bank-account', category: 'financial', name: 'Bank account (labeled)',
    description: '9-18 digit number preceded by an account-number label.',
    pattern: '\\b(?:a\\/c|acct|account)\\s*(?:no|number|#)?\\s*[:#]?\\s*\\d{9,18}\\b', ignore_case: true,
    risk: 'med', compliance: 'RBI', recommended: false,
    message: 'Blocked: request appears to contain a bank account number.',
  },
  {
    id: 'fin-iban', category: 'financial', name: 'IBAN',
    description: 'International Bank Account Number.',
    pattern: '\\b[A-Z]{2}\\d{2}[A-Z0-9]{11,30}\\b', ignore_case: false,
    risk: 'med', compliance: 'GDPR/PSD2', recommended: false,
    message: 'Blocked: request appears to contain an IBAN.',
  },
  {
    id: 'fin-gstin', category: 'financial', name: 'India GSTIN',
    description: 'GST identification number, e.g. 22ABCDE1234F1Z5.',
    pattern: '\\b\\d{2}[A-Z]{5}\\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]\\b', ignore_case: false,
    risk: 'low', compliance: 'GST', recommended: true,
    message: 'Blocked: request appears to contain a GSTIN.',
  },
  {
    id: 'fin-swift-bic', category: 'financial', name: 'SWIFT/BIC (labeled)',
    description: 'A SWIFT/BIC code preceded by a swift/bic label.',
    pattern: '\\b(?:swift|bic)\\b\\s*[:#]?\\s*[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?\\b', ignore_case: true,
    risk: 'low', compliance: 'RBI', recommended: false,
    message: 'Blocked: request appears to contain a SWIFT/BIC code.',
  },
  {
    id: 'fin-upi-vpa', category: 'financial', name: 'UPI VPA',
    description: 'UPI virtual payment address, e.g. name@okhdfcbank.',
    pattern: '\\b[\\w.\\-]{2,}@(?:okhdfcbank|oksbi|okicici|okaxis|paytm|ybl|ibl|axl|upi)\\b', ignore_case: true,
    risk: 'low', compliance: 'NPCI', recommended: true,
    message: 'Blocked: request appears to contain a UPI VPA.',
  },

  // ── C — Secrets / credentials ───────────────────────────────────────────
  {
    id: 'secret-aws-akid', category: 'secrets', name: 'AWS Access Key ID',
    description: 'AWS access key id (AKIA…/ASIA…).',
    pattern: '\\b(?:AKIA|ASIA)[0-9A-Z]{16}\\b', ignore_case: false,
    risk: 'low', compliance: 'SOC2/ISO27001', recommended: true,
    message: 'Blocked: an AWS access key was detected. Never paste credentials into prompts.',
  },
  {
    id: 'secret-aws-secret', category: 'secrets', name: 'AWS Secret Key (labeled)',
    description: 'A 40-char secret following aws_secret_access_key.',
    pattern: '\\baws_secret_access_key\\b\\s*[=:]\\s*["\']?[A-Za-z0-9/+]{40}["\']?', ignore_case: true,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: an AWS secret access key was detected.',
  },
  {
    id: 'secret-private-key', category: 'secrets', name: 'Private key block',
    description: 'PEM private key header (RSA/EC/DSA/OpenSSH/PGP).',
    pattern: '-----BEGIN (?:RSA |EC |DSA |OPENSSH |PGP )?PRIVATE KEY-----', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: a private key block was detected.',
  },
  {
    id: 'secret-generic-assignment', category: 'secrets', name: 'Generic secret assignment',
    description: 'api_key/secret/token/password = <16+ chars>. High FP in config-heavy code.',
    pattern: '\\b(?:api[_-]?key|secret|token|passwd|password)\\b\\s*[=:]\\s*["\']?[A-Za-z0-9_\\-]{16,}', ignore_case: true,
    risk: 'high', compliance: 'SOC2', recommended: false,
    message: 'Blocked: a hardcoded secret/credential was detected.',
  },
  {
    id: 'secret-jwt', category: 'secrets', name: 'JWT',
    description: 'JSON Web Token (eyJ….….…). Can appear in legitimate dev payloads.',
    pattern: '\\beyJ[A-Za-z0-9_\\-]{8,}\\.[A-Za-z0-9_\\-]{8,}\\.[A-Za-z0-9_\\-]{8,}\\b', ignore_case: false,
    risk: 'high', compliance: 'SOC2', recommended: false,
    message: 'Blocked: a JWT/access token was detected.',
  },
  {
    id: 'secret-slack-github', category: 'secrets', name: 'Slack/GitHub token',
    description: 'Slack (xox…) or GitHub (ghp_/gho_…) tokens.',
    pattern: '\\b(?:xox[baprs]-[A-Za-z0-9-]{10,}|gh[pousr]_[A-Za-z0-9]{36,})\\b', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: a Slack/GitHub access token was detected.',
  },
  {
    id: 'secret-bearer-header', category: 'secrets', name: 'Bearer token header',
    description: 'An Authorization: Bearer <token> header value.',
    pattern: '\\bauthorization\\b\\s*:\\s*bearer\\s+[A-Za-z0-9._\\-]{20,}', ignore_case: true,
    risk: 'med', compliance: 'SOC2', recommended: false,
    message: 'Blocked: an Authorization bearer token was detected.',
  },
  {
    id: 'secret-db-conn', category: 'secrets', name: 'DB connection string',
    description: 'mongodb/postgres/mysql/redis URI carrying user:password@host.',
    pattern: '\\b(?:mongodb(?:\\+srv)?|postgres(?:ql)?|mysql|redis):\\/\\/[^\\s"\']*:[^\\s"\']*@', ignore_case: true,
    risk: 'med', compliance: 'SOC2', recommended: false,
    message: 'Blocked: a database connection string with embedded credentials was detected.',
  },

  // ── D — Prompt injection / jailbreak ────────────────────────────────────
  {
    id: 'inj-ignore-previous', category: 'injection', name: 'Ignore previous instructions',
    description: 'Classic injection: "ignore previous/above instructions".',
    pattern: '\\bignore\\s+(?:all\\s+)?(?:previous|prior|above)\\s+(?:instructions|prompts?|context)\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request looks like a prompt-injection attempt.',
  },
  {
    id: 'inj-system-exfil', category: 'injection', name: 'System-prompt exfiltration',
    description: 'Attempts to reveal/print/repeat the system or developer prompt.',
    pattern: '\\b(?:reveal|print|show|repeat|disclose)\\b.{0,30}\\b(?:system|developer)\\s+prompt\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request attempts to extract the system prompt.',
  },
  {
    id: 'inj-jailbreak-persona', category: 'injection', name: 'Jailbreak personas',
    description: 'DAN / developer-mode / "unfiltered" jailbreak phrasings.',
    pattern: '\\b(?:do anything now|DAN mode|developer mode|jailbreak|unfiltered|without any restrictions)\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request looks like a jailbreak attempt.',
  },
  {
    id: 'inj-role-override', category: 'injection', name: 'Role override',
    description: '"You are no longer…" / "disregard your guidelines/rules".',
    pattern: '\\byou are no longer\\b|\\bdisregard\\s+(?:your\\s+)?(?:guidelines|rules|policy|instructions)\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request attempts to override the model\'s instructions.',
  },

  // ── E — Regulatory / market conduct ─────────────────────────────────────
  {
    id: 'reg-mnpi', category: 'compliance', name: 'MNPI / insider trading',
    description: 'Material non-public information / insider-trading / front-running terms.',
    pattern: '\\b(?:material non[- ]?public information|MNPI|insider (?:trading|information)|front[- ]?running)\\b', ignore_case: true,
    risk: 'med', compliance: 'SEBI/SEC', recommended: false,
    message: 'Blocked: the request references material non-public information (regulated).',
  },
  {
    id: 'reg-confidential', category: 'compliance', name: 'Confidential markers',
    description: 'Document classification markers (strictly confidential, do not distribute…).',
    pattern: '\\b(?:strictly confidential|internal use only|do not distribute|proprietary and confidential)\\b', ignore_case: true,
    risk: 'med', compliance: 'Internal/ISO27001', recommended: false,
    message: 'Blocked: the request contains content marked confidential.',
  },

  // ════════════════════════════════════════════════════════════════════════
  // Expansion pack (+30) — broader PII, securities/markets identifiers, SaaS
  // secrets, OWASP-LLM injection, data-exfiltration/SSRF, market-conduct, and
  // content safety. Distinct from the 30 above; tuned for regulated finance.
  // ════════════════════════════════════════════════════════════════════════

  // ── A — PII / Identity (more jurisdictions) ─────────────────────────────
  {
    id: 'pii-india-dl', category: 'pii', name: 'India driving license',
    description: 'State + RTO + year + serial driving license number, e.g. MH12 2011 0012345.',
    pattern: '\\b[A-Z]{2}\\d{2}\\s?\\d{4}\\s?\\d{7}\\b', ignore_case: false,
    risk: 'med', compliance: 'DPDP', recommended: false,
    message: 'Blocked: request appears to contain a driving license number.',
  },
  {
    id: 'pii-india-vehicle', category: 'pii', name: 'India vehicle registration',
    description: 'Vehicle number plate, e.g. KA05MH1234.',
    pattern: '\\b[A-Z]{2}\\s?\\d{1,2}\\s?[A-Z]{1,3}\\s?\\d{4}\\b', ignore_case: false,
    risk: 'med', compliance: 'DPDP', recommended: false,
    message: 'Blocked: request appears to contain a vehicle registration number.',
  },
  {
    id: 'pii-uk-nino', category: 'pii', name: 'UK National Insurance no.',
    description: 'UK NINO, e.g. QQ 12 34 56 C.',
    pattern: '\\b[A-CEGHJ-PR-TW-Z]{2}\\s?\\d{2}\\s?\\d{2}\\s?\\d{2}\\s?[A-D]\\b', ignore_case: false,
    risk: 'low', compliance: 'UK-GDPR', recommended: true,
    message: 'Blocked: request appears to contain a UK National Insurance number.',
  },
  {
    id: 'pii-us-itin', category: 'pii', name: 'US ITIN',
    description: 'US Individual Taxpayer Identification Number (9XX-7X-XXXX).',
    pattern: '\\b9\\d{2}[ \\-][7-8]\\d[ \\-]\\d{4}\\b', ignore_case: false,
    risk: 'low', compliance: 'IRS/GLBA', recommended: true,
    message: 'Blocked: request appears to contain a US ITIN.',
  },

  // ── B — Financial / securities identifiers ──────────────────────────────
  {
    id: 'fin-isin', category: 'financial', name: 'ISIN (security id)',
    description: 'International Securities Identification Number, e.g. US0378331005.',
    pattern: '\\b[A-Z]{2}[A-Z0-9]{9}\\d\\b', ignore_case: false,
    risk: 'med', compliance: 'SEBI/ISO6166', recommended: false,
    message: 'Blocked: request appears to contain an ISIN security identifier.',
  },
  {
    id: 'fin-india-cin', category: 'financial', name: 'India CIN',
    description: 'Corporate Identification Number, e.g. L17110MH1973PLC019786.',
    pattern: '\\b[LU]\\d{5}[A-Z]{2}\\d{4}[A-Z]{3}\\d{6}\\b', ignore_case: false,
    risk: 'low', compliance: 'MCA', recommended: false,
    message: 'Blocked: request appears to contain a Corporate Identification Number (CIN).',
  },
  {
    id: 'fin-india-tan', category: 'financial', name: 'India TAN',
    description: 'Tax Deduction Account Number, e.g. RAJA99999B.',
    pattern: '\\b[A-Z]{4}\\d{5}[A-Z]\\b', ignore_case: false,
    risk: 'med', compliance: 'Income-Tax', recommended: false,
    message: 'Blocked: request appears to contain a TAN.',
  },
  {
    id: 'fin-india-demat', category: 'financial', name: 'India demat / DP ID',
    description: 'NSDL/CDSL demat account (IN + 14 digits) or a labeled DP/BO/client id.',
    pattern: '\\bIN\\d{14}\\b|\\b(?:demat|dp id|bo id|client id)\\b\\s*[:#]?\\s*\\d{8,16}\\b', ignore_case: true,
    risk: 'med', compliance: 'NSDL/CDSL', recommended: false,
    message: 'Blocked: request appears to contain a demat / DP account number.',
  },
  {
    id: 'fin-lei', category: 'financial', name: 'Legal Entity Identifier',
    description: '20-char LEI used in trade reporting.',
    pattern: '\\b[A-Z0-9]{18}\\d{2}\\b', ignore_case: false,
    risk: 'med', compliance: 'GLEIF/MiFID', recommended: false,
    message: 'Blocked: request appears to contain a Legal Entity Identifier (LEI).',
  },
  {
    id: 'fin-us-aba', category: 'financial', name: 'US ABA routing (labeled)',
    description: '9-digit ABA/RTN routing number preceded by a routing label.',
    pattern: '\\b(?:aba|routing(?:\\s?number)?|rtn)\\b\\s*[:#]?\\s*\\d{9}\\b', ignore_case: true,
    risk: 'low', compliance: 'US-NACHA', recommended: false,
    message: 'Blocked: request appears to contain a bank routing number.',
  },
  {
    id: 'fin-crypto-wallet', category: 'financial', name: 'Crypto wallet address',
    description: 'Bitcoin (legacy/bech32) or Ethereum wallet address.',
    pattern: '\\b(?:bc1[a-z0-9]{25,39}|[13][a-km-zA-HJ-NP-Z1-9]{25,34}|0x[a-fA-F0-9]{40})\\b', ignore_case: false,
    risk: 'med', compliance: 'AML/Crypto', recommended: false,
    message: 'Blocked: request appears to contain a cryptocurrency wallet address.',
  },

  // ── C — SaaS / cloud secrets ────────────────────────────────────────────
  {
    id: 'secret-google-api-key', category: 'secrets', name: 'Google API key',
    description: 'Google/GCP API key (AIza…).',
    pattern: '\\bAIza[0-9A-Za-z\\-_]{35}\\b', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: a Google API key was detected.',
  },
  {
    id: 'secret-github-pat', category: 'secrets', name: 'GitHub fine-grained PAT',
    description: 'GitHub fine-grained personal access token (github_pat_…).',
    pattern: '\\bgithub_pat_[0-9A-Za-z_]{22,}\\b', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: a GitHub access token was detected.',
  },
  {
    id: 'secret-stripe-key', category: 'secrets', name: 'Stripe secret key',
    description: 'Stripe live/test secret or restricted key (sk_live_…/rk_…).',
    pattern: '\\b(?:sk|rk)_(?:live|test)_[0-9A-Za-z]{16,}\\b', ignore_case: false,
    risk: 'low', compliance: 'PCI-DSS', recommended: true,
    message: 'Blocked: a Stripe secret key was detected.',
  },
  {
    id: 'secret-llm-provider-key', category: 'secrets', name: 'Third-party LLM key',
    description: 'OpenAI / Anthropic API key (sk-…/sk-ant-…) — keep traffic on the on-prem gateway.',
    pattern: '\\b(?:sk-ant-[A-Za-z0-9_\\-]{20,}|sk-[A-Za-z0-9]{20,})\\b', ignore_case: false,
    risk: 'med', compliance: 'Data-egress', recommended: true,
    message: 'Blocked: a third-party LLM provider key was detected. Route requests through the gateway.',
  },
  {
    id: 'secret-gcp-service-account', category: 'secrets', name: 'GCP service account JSON',
    description: 'Leaked service-account key file ("type": "service_account").',
    pattern: '"type"\\s*:\\s*"service_account"', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: a GCP service-account key file was detected.',
  },
  {
    id: 'secret-basic-auth-url', category: 'secrets', name: 'Credentials in URL',
    description: 'user:password@host embedded in an http(s) URL.',
    pattern: '\\bhttps?:\\/\\/[^\\s\\/:@]+:[^\\s\\/:@]+@', ignore_case: true,
    risk: 'med', compliance: 'SOC2', recommended: false,
    message: 'Blocked: a URL with embedded credentials was detected.',
  },

  // ── D — Prompt injection / jailbreak (OWASP-LLM) ────────────────────────
  {
    id: 'inj-unicode-bidi', category: 'injection', name: 'Hidden Unicode / zero-width',
    description: 'Zero-width or bidi-override characters used to hide injected instructions.',
    pattern: '[\\u200B-\\u200D\\u2060\\u202D\\u202E\\u2066-\\u2069\\uFEFF]', ignore_case: false,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: hidden / zero-width Unicode characters were detected.',
  },
  {
    id: 'inj-repeat-above', category: 'injection', name: 'Repeat everything above',
    description: 'Prompt-dump attempt: "repeat/print everything above".',
    pattern: '\\b(?:repeat|print|output|echo)\\b.{0,30}\\b(?:everything above|all (?:the )?text above|the words above|the prompt above)\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request attempts to dump the prompt context.',
  },
  {
    id: 'inj-base64-blob', category: 'injection', name: 'Large base64 blob',
    description: 'Very long base64 string — possible obfuscated payload or exfil (high FP).',
    pattern: '\\b[A-Za-z0-9+/]{200,}={0,2}\\b', ignore_case: false,
    risk: 'high', compliance: 'OWASP-LLM', recommended: false,
    message: 'Blocked: a large encoded payload was detected.',
  },
  {
    id: 'inj-role-marker', category: 'injection', name: 'Injected role marker',
    description: 'Fake "system:"/"assistant:" role line trying to override behaviour.',
    pattern: '(?:^|\\n)\\s*(?:system|assistant)\\s*:\\s*(?:you (?:are|must|will)|ignore|disregard)\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: an injected role marker was detected.',
  },

  // ── E — Data exfiltration & SSRF ────────────────────────────────────────
  {
    id: 'exfil-cloud-metadata', category: 'exfil', name: 'Cloud metadata / SSRF',
    description: 'Cloud metadata endpoints (169.254.169.254, metadata.google.internal…).',
    pattern: '\\b(?:169\\.254\\.169\\.254|metadata\\.google\\.internal|metadata\\.azure\\.com|100\\.100\\.100\\.200)\\b', ignore_case: true,
    risk: 'low', compliance: 'SSRF/OWASP', recommended: true,
    message: 'Blocked: a cloud metadata / SSRF endpoint was referenced.',
  },
  {
    id: 'exfil-curl-wget', category: 'exfil', name: 'Outbound fetch command',
    description: 'curl/wget/Invoke-WebRequest to a URL — possible exfil (high FP in dev).',
    pattern: '\\b(?:curl|wget|Invoke-WebRequest)\\b[^\\n]{0,60}https?:\\/\\/', ignore_case: true,
    risk: 'high', compliance: 'Data-exfil', recommended: false,
    message: 'Blocked: an outbound fetch command was detected.',
  },
  {
    id: 'exfil-md-image', category: 'exfil', name: 'Markdown image exfil',
    description: 'Markdown image whose URL carries query parameters — a known exfil channel.',
    pattern: '!\\[[^\\]]*\\]\\(\\s*https?:\\/\\/[^)]*[?&][^)]*=', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM02', recommended: false,
    message: 'Blocked: a markdown image data-exfiltration attempt was detected.',
  },
  {
    id: 'exfil-mac-address', category: 'exfil', name: 'MAC address',
    description: 'Hardware MAC address (network / asset identifier).',
    pattern: '\\b(?:[0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}\\b', ignore_case: false,
    risk: 'med', compliance: 'Asset/ISO27001', recommended: false,
    message: 'Blocked: a hardware MAC address was detected.',
  },

  // ── F — Regulatory / market conduct ─────────────────────────────────────
  {
    id: 'reg-aml', category: 'compliance', name: 'AML / money laundering',
    description: 'Money-laundering, structuring, smurfing, hawala, shell-company terms.',
    pattern: '\\b(?:money\\s?launder(?:ing)?|structuring|smurfing|layering|hawala|shell\\s+compan(?:y|ies))\\b', ignore_case: true,
    risk: 'med', compliance: 'PMLA/AML', recommended: false,
    message: 'Blocked: the request references money-laundering activity (regulated).',
  },
  {
    id: 'reg-sanctions', category: 'compliance', name: 'Sanctions / OFAC',
    description: 'OFAC, SDN list, sanctioned/denied party, embargo references.',
    pattern: '\\b(?:OFAC|SDN\\s?list|sanctioned\\s+(?:entity|party|individual)|embargoed|denied\\s+party)\\b', ignore_case: true,
    risk: 'med', compliance: 'OFAC/Sanctions', recommended: false,
    message: 'Blocked: the request references sanctioned parties (regulated).',
  },
  {
    id: 'reg-market-manipulation', category: 'compliance', name: 'Market manipulation',
    description: 'Pump-and-dump, wash trading, spoofing, front-running, cornering.',
    pattern: '\\b(?:pump\\s+and\\s+dump|wash\\s+trad(?:e|ing)|spoofing|front[- ]?running|cornering\\s+the\\s+market)\\b', ignore_case: true,
    risk: 'med', compliance: 'SEBI/SEC', recommended: false,
    message: 'Blocked: the request references market-manipulation activity (regulated).',
  },

  // ── G — Content safety ──────────────────────────────────────────────────
  {
    id: 'safety-profanity', category: 'safety', name: 'Profanity',
    description: 'Common profanity (mild list) for professional-tone enforcement.',
    pattern: '\\b(?:f\\*?u\\*?ck|sh\\*?it|assholes?|bastards?|bitch|motherf\\w*)\\b', ignore_case: true,
    risk: 'med', compliance: 'Content-safety', recommended: false,
    message: 'Blocked: the request contains profanity.',
  },
  {
    id: 'safety-violence', category: 'safety', name: 'Violence / threats',
    description: 'Explicit threats or weapon-making requests.',
    pattern: '\\b(?:kill (?:him|her|them|you)|i will (?:kill|hurt|harm)|bomb threat|how to (?:make|build) a (?:bomb|weapon|gun))\\b', ignore_case: true,
    risk: 'med', compliance: 'Content-safety', recommended: false,
    message: 'Blocked: the request contains violent or threatening content.',
  },

  // ════════════════════════════════════════════════════════════════════════
  // Expansion pack (+40) → 100 total. Global PII, PHI/HIPAA, deeper PCI &
  // securities IDs, more SaaS/cloud secrets, OWASP-LLM injection variants,
  // network/SSRF egress, and market-conduct/governance. Distinct from above.
  // ════════════════════════════════════════════════════════════════════════

  // ── A — PII / Identity (more jurisdictions) ─────────────────────────────
  {
    id: 'pii-canada-sin', category: 'pii', name: 'Canada SIN (labeled)',
    description: 'Social Insurance Number preceded by a SIN label.',
    pattern: '\\b(?:sin|social insurance)\\b\\s*[:#]?\\s*\\d{3}[\\s-]?\\d{3}[\\s-]?\\d{3}\\b', ignore_case: true,
    risk: 'low', compliance: 'PIPEDA', recommended: false,
    message: 'Blocked: request appears to contain a Canadian SIN.',
  },
  {
    id: 'pii-australia-tfn', category: 'pii', name: 'Australia TFN (labeled)',
    description: 'Tax File Number preceded by a TFN label.',
    pattern: '\\b(?:tfn|tax file number)\\b\\s*[:#]?\\s*\\d{3}\\s?\\d{3}\\s?\\d{3}\\b', ignore_case: true,
    risk: 'low', compliance: 'AU-Privacy', recommended: false,
    message: 'Blocked: request appears to contain an Australian Tax File Number.',
  },
  {
    id: 'pii-singapore-nric', category: 'pii', name: 'Singapore NRIC/FIN',
    description: 'Singapore NRIC/FIN, e.g. S1234567A.',
    pattern: '\\b[STFGM]\\d{7}[A-Z]\\b', ignore_case: false,
    risk: 'med', compliance: 'SG-PDPA', recommended: false,
    message: 'Blocked: request appears to contain a Singapore NRIC/FIN.',
  },
  {
    id: 'pii-brazil-cpf', category: 'pii', name: 'Brazil CPF',
    description: 'Brazilian CPF, e.g. 123.456.789-09.',
    pattern: '\\b\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2}\\b', ignore_case: false,
    risk: 'low', compliance: 'LGPD', recommended: true,
    message: 'Blocked: request appears to contain a Brazilian CPF.',
  },
  {
    id: 'pii-passport-labeled', category: 'pii', name: 'Passport no. (labeled)',
    description: 'A passport number preceded by a passport label (any country).',
    pattern: '\\bpassport\\b\\s*(?:no|number|#)?\\s*[:#]?\\s*[A-Z0-9]{6,9}\\b', ignore_case: true,
    risk: 'med', compliance: 'GDPR/DPDP', recommended: false,
    message: 'Blocked: request appears to contain a passport number.',
  },
  {
    id: 'pii-eu-vat', category: 'pii', name: 'EU VAT number',
    description: 'EU VAT registration number (AT/DE/FR/GB/IT/ES forms).',
    pattern: '\\b(?:ATU\\d{8}|DE\\d{9}|FR[A-Z0-9]{2}\\d{9}|GB\\d{9}|IT\\d{11}|ES[A-Z0-9]\\d{7}[A-Z0-9])\\b', ignore_case: false,
    risk: 'med', compliance: 'EU-VAT', recommended: false,
    message: 'Blocked: request appears to contain an EU VAT number.',
  },
  {
    id: 'pii-biometric', category: 'pii', name: 'Biometric references',
    description: 'Biometric / sensitive-category terms (fingerprint, iris, facial recognition…).',
    pattern: '\\b(?:biometric|fingerprint|retina scan|iris scan|facial recognition|voiceprint)\\b', ignore_case: true,
    risk: 'med', compliance: 'DPDP/GDPR-Art9', recommended: false,
    message: 'Blocked: the request references biometric / sensitive personal data.',
  },

  // ── B — Health data (PHI / HIPAA) ───────────────────────────────────────
  {
    id: 'phi-us-npi', category: 'phi', name: 'US NPI (labeled)',
    description: '10-digit National Provider Identifier preceded by an NPI label.',
    pattern: '\\bnpi\\b\\s*[:#]?\\s*\\d{10}\\b', ignore_case: true,
    risk: 'low', compliance: 'HIPAA', recommended: false,
    message: 'Blocked: request appears to contain a US National Provider Identifier.',
  },
  {
    id: 'phi-dea-number', category: 'phi', name: 'DEA number',
    description: 'US DEA registration number, e.g. AB1234567.',
    pattern: '\\b[ABFGMPRX][A-Z]\\d{7}\\b', ignore_case: false,
    risk: 'med', compliance: 'HIPAA/DEA', recommended: false,
    message: 'Blocked: request appears to contain a DEA registration number.',
  },
  {
    id: 'phi-mrn-labeled', category: 'phi', name: 'Medical record no. (labeled)',
    description: 'A medical record number preceded by an MRN label.',
    pattern: '\\b(?:mrn|medical record (?:no|number))\\b\\s*[:#]?\\s*\\d{6,10}\\b', ignore_case: true,
    risk: 'low', compliance: 'HIPAA', recommended: false,
    message: 'Blocked: request appears to contain a medical record number.',
  },
  {
    id: 'phi-icd10', category: 'phi', name: 'ICD-10 diagnosis code',
    description: 'ICD-10 diagnosis code, e.g. E11.9 (high false-positive risk).',
    pattern: '\\b[A-TV-Z]\\d{2}(?:\\.\\d{1,4})?\\b', ignore_case: false,
    risk: 'high', compliance: 'HIPAA', recommended: false,
    message: 'Blocked: request appears to contain an ICD-10 diagnosis code.',
  },
  {
    id: 'phi-insurance-labeled', category: 'phi', name: 'Health insurance id (labeled)',
    description: 'Member/policy/claim id preceded by an insurance label.',
    pattern: '\\b(?:member id|policy (?:no|number)|claim (?:no|number))\\b\\s*[:#]?\\s*[A-Z0-9]{6,15}\\b', ignore_case: true,
    risk: 'med', compliance: 'HIPAA', recommended: false,
    message: 'Blocked: request appears to contain a health insurance identifier.',
  },

  // ── C — Financial / PCI / securities (deeper) ───────────────────────────
  {
    id: 'fin-card-track', category: 'financial', name: 'Card magnetic-stripe data',
    description: 'Track 1/2 magnetic-stripe data (%B…^ or ;…=…).',
    pattern: '(?:%B\\d{12,19}\\^|;\\d{12,19}=\\d{4,})', ignore_case: false,
    risk: 'low', compliance: 'PCI-DSS', recommended: true,
    message: 'Blocked: card magnetic-stripe (track) data was detected.',
  },
  {
    id: 'fin-card-pin-labeled', category: 'financial', name: 'Card PIN (labeled)',
    description: 'A 4-6 digit PIN preceded by a card/ATM PIN label.',
    pattern: '\\b(?:card pin|atm pin|pin code)\\b\\s*[:#]?\\s*\\d{4,6}\\b', ignore_case: true,
    risk: 'med', compliance: 'PCI-DSS', recommended: false,
    message: 'Blocked: a card PIN was detected.',
  },
  {
    id: 'fin-diners-jcb', category: 'financial', name: 'Diners/JCB card',
    description: 'Diners Club and JCB card numbers (not covered by the main card rule).',
    pattern: '\\b(?:3(?:0[0-5]|[68]\\d)\\d{11}|35\\d{14}|(?:2131|1800)\\d{11})\\b', ignore_case: false,
    risk: 'low', compliance: 'PCI-DSS', recommended: true,
    message: 'Blocked: a payment card number (Diners/JCB) was detected.',
  },
  {
    id: 'fin-micr-labeled', category: 'financial', name: 'India MICR (labeled)',
    description: '9-digit cheque MICR code preceded by a MICR label.',
    pattern: '\\bmicr\\b\\s*[:#]?\\s*\\d{9}\\b', ignore_case: true,
    risk: 'low', compliance: 'RBI', recommended: false,
    message: 'Blocked: request appears to contain a cheque MICR code.',
  },
  {
    id: 'fin-mf-folio', category: 'financial', name: 'Mutual fund folio (labeled)',
    description: 'A mutual-fund folio number preceded by a folio label.',
    pattern: '\\bfolio(?:\\s?(?:no|number))?\\b\\s*[:#]?\\s*\\d{6,12}\\b', ignore_case: true,
    risk: 'med', compliance: 'SEBI', recommended: false,
    message: 'Blocked: request appears to contain a mutual-fund folio number.',
  },
  {
    id: 'fin-trading-account', category: 'financial', name: 'Trading account / UCC (labeled)',
    description: 'Broker trading account / client code / UCC preceded by a label.',
    pattern: '\\b(?:trading (?:a\\/c|account)|client code|ucc)\\b\\s*[:#]?\\s*[A-Z0-9]{4,12}\\b', ignore_case: true,
    risk: 'med', compliance: 'SEBI', recommended: false,
    message: 'Blocked: request appears to contain a trading account / client code.',
  },

  // ── D — SaaS / cloud secrets (more providers) ───────────────────────────
  {
    id: 'secret-azure-storage', category: 'secrets', name: 'Azure storage connection',
    description: 'Azure storage account connection string (AccountKey=…).',
    pattern: '\\bDefaultEndpointsProtocol=https?;AccountName=[^;]+;AccountKey=[^;\\s]+', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: an Azure storage connection string was detected.',
  },
  {
    id: 'secret-twilio-sid', category: 'secrets', name: 'Twilio Account SID',
    description: 'Twilio Account SID (AC + 32 hex).',
    pattern: '\\bAC[0-9a-fA-F]{32}\\b', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: a Twilio Account SID was detected.',
  },
  {
    id: 'secret-sendgrid', category: 'secrets', name: 'SendGrid API key',
    description: 'SendGrid API key (SG.….…).',
    pattern: '\\bSG\\.[\\w\\-]{16,32}\\.[\\w\\-]{16,64}\\b', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: a SendGrid API key was detected.',
  },
  {
    id: 'secret-slack-webhook', category: 'secrets', name: 'Slack webhook URL',
    description: 'Incoming Slack webhook URL (hooks.slack.com/services/…).',
    pattern: '\\bhttps:\\/\\/hooks\\.slack\\.com\\/services\\/T[0-9A-Z]+\\/B[0-9A-Z]+\\/[0-9A-Za-z]+', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: a Slack webhook URL was detected.',
  },
  {
    id: 'secret-mailgun', category: 'secrets', name: 'Mailgun API key',
    description: 'Mailgun API key (key-<32 hex>).',
    pattern: '\\bkey-[0-9a-f]{32}\\b', ignore_case: false,
    risk: 'med', compliance: 'SOC2', recommended: false,
    message: 'Blocked: a Mailgun API key was detected.',
  },
  {
    id: 'secret-npm-token', category: 'secrets', name: 'npm access token',
    description: 'npm automation/access token (npm_…).',
    pattern: '\\bnpm_[A-Za-z0-9]{36}\\b', ignore_case: false,
    risk: 'low', compliance: 'SOC2', recommended: true,
    message: 'Blocked: an npm access token was detected.',
  },

  // ── E — Prompt injection / jailbreak (OWASP-LLM variants) ───────────────
  {
    id: 'inj-print-instructions', category: 'injection', name: 'Print your instructions',
    description: 'Asks the model to reveal its own instructions/guidelines/system message.',
    pattern: '\\b(?:print|show|list|output|reveal)\\b.{0,20}\\byour (?:instructions|guidelines|rules|system message|prompt)\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request attempts to extract the model instructions.',
  },
  {
    id: 'inj-pretend-roleplay', category: 'injection', name: 'Pretend / roleplay bypass',
    description: '"Pretend you are…", "roleplay as…", "act as if you have no rules".',
    pattern: '\\b(?:pretend (?:you are|to be)|role-?play as|simulate being|act as if you (?:are|have no))\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request looks like a roleplay jailbreak.',
  },
  {
    id: 'inj-output-hijack', category: 'injection', name: 'Output hijack',
    description: 'Forces a fixed reply prefix: "begin your response with…".',
    pattern: '\\b(?:start|begin|prefix) your (?:response|reply|answer|output) with\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request attempts to hijack the response output.',
  },
  {
    id: 'inj-encode-answer', category: 'injection', name: 'Encode the answer (filter bypass)',
    description: 'Asks for base64/rot13/hex-encoded output to evade content filters.',
    pattern: '\\b(?:base64|rot13|hex|morse)[- ]?encode (?:your|the) (?:answer|response|output|reply)\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request attempts to encode output to bypass filters.',
  },
  {
    id: 'inj-override-safety', category: 'injection', name: 'Disable safety / filters',
    description: '"Override/bypass/disable the safety filter / moderation / guardrails".',
    pattern: '\\b(?:override|bypass|disable|turn off|ignore)\\b.{0,20}\\b(?:safety|content filter|moderation|guardrails?|restrictions?)\\b', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: the request attempts to disable safety controls.',
  },
  {
    id: 'inj-fake-tags', category: 'injection', name: 'Fake system/admin tags',
    description: 'Injected pseudo-tags like <system>, </instructions>, <admin>.',
    pattern: '<\\/?(?:system|instructions?|admin|developer)>', ignore_case: true,
    risk: 'med', compliance: 'OWASP-LLM01', recommended: false,
    message: 'Blocked: injected system/admin tags were detected.',
  },

  // ── F — Data exfiltration / SSRF / network ──────────────────────────────
  {
    id: 'exfil-private-ip', category: 'exfil', name: 'Private IP (RFC1918)',
    description: 'Internal IP ranges (10.x, 192.168.x, 172.16–31.x). High FP in infra docs.',
    pattern: '\\b(?:10(?:\\.\\d{1,3}){3}|192\\.168(?:\\.\\d{1,3}){2}|172\\.(?:1[6-9]|2\\d|3[01])(?:\\.\\d{1,3}){2})\\b', ignore_case: false,
    risk: 'high', compliance: 'Network/SSRF', recommended: false,
    message: 'Blocked: an internal/private IP address was detected.',
  },
  {
    id: 'exfil-s3-bucket', category: 'exfil', name: 'S3 bucket URL',
    description: 's3:// URI or *.s3.amazonaws.com bucket reference.',
    pattern: '\\b(?:s3:\\/\\/[a-z0-9.\\-]{3,63}|[a-z0-9.\\-]{3,63}\\.s3(?:[.\\-][a-z0-9\\-]+)?\\.amazonaws\\.com)\\b', ignore_case: true,
    risk: 'med', compliance: 'Data-exfil', recommended: false,
    message: 'Blocked: an S3 bucket reference was detected.',
  },
  {
    id: 'exfil-internal-hostname', category: 'exfil', name: 'Internal hostname',
    description: 'Hostnames ending in .internal/.local/.corp/.intranet/.lan.',
    pattern: '\\b[a-z0-9\\-]+\\.(?:internal|local|corp|intranet|lan)\\b', ignore_case: true,
    risk: 'med', compliance: 'Network', recommended: false,
    message: 'Blocked: an internal hostname was referenced.',
  },
  {
    id: 'exfil-file-scheme', category: 'exfil', name: 'file:// or smb:// path',
    description: 'Local/remote file-system URIs (file://, smb://).',
    pattern: '\\b(?:file:\\/\\/\\/?|smb:\\/\\/)[^\\s"\']+', ignore_case: true,
    risk: 'med', compliance: 'Data-exfil', recommended: false,
    message: 'Blocked: a local/remote file path URI was detected.',
  },
  {
    id: 'exfil-uri-scheme', category: 'exfil', name: 'Dangerous URI scheme',
    description: 'data:text/html, javascript:, vbscript: URIs (XSS/exfil vectors).',
    pattern: '\\b(?:data:text\\/html|javascript:|vbscript:)', ignore_case: true,
    risk: 'med', compliance: 'XSS/Exfil', recommended: false,
    message: 'Blocked: a dangerous URI scheme was detected.',
  },

  // ── G — Regulatory / governance ─────────────────────────────────────────
  {
    id: 'reg-bribery', category: 'compliance', name: 'Bribery / FCPA',
    description: 'Bribery, kickback, facilitation/grease payment, "under the table".',
    pattern: '\\b(?:bribe|bribery|kickback|facilitation payment|grease payment|under the table)\\b', ignore_case: true,
    risk: 'med', compliance: 'FCPA/UKBA', recommended: false,
    message: 'Blocked: the request references bribery/corruption (regulated).',
  },
  {
    id: 'reg-legal-privilege', category: 'compliance', name: 'Legal privilege markers',
    description: 'Attorney-client privileged / privileged and confidential / work product.',
    pattern: '\\b(?:attorney[- ]client privileged?|legally privileged|privileged and confidential|attorney work product)\\b', ignore_case: true,
    risk: 'med', compliance: 'Legal', recommended: false,
    message: 'Blocked: the request contains legally privileged content.',
  },
  {
    id: 'reg-chinese-wall', category: 'compliance', name: 'Information barrier breach',
    description: 'Chinese wall / information barrier / wall-crossing references.',
    pattern: '\\b(?:chinese wall|information barrier|cross the wall|wall[- ]cross(?:ing)?)\\b', ignore_case: true,
    risk: 'med', compliance: 'SEBI/Conduct', recommended: false,
    message: 'Blocked: the request references an information-barrier breach (regulated).',
  },
  {
    id: 'reg-tax-evasion', category: 'compliance', name: 'Tax evasion',
    description: 'Tax evasion, hide income, undisclosed offshore, round-tripping.',
    pattern: '\\b(?:tax evasion|evade taxes|hide income|undisclosed offshore|round[- ]?trip(?:ping)?)\\b', ignore_case: true,
    risk: 'med', compliance: 'Tax/AML', recommended: false,
    message: 'Blocked: the request references tax evasion (regulated).',
  },
  {
    id: 'reg-gdpr-dsar', category: 'compliance', name: 'GDPR data-subject request',
    description: 'Right to erasure / right to be forgotten / DSAR references.',
    pattern: '\\b(?:right to (?:erasure|be forgotten)|data subject (?:access )?request|dsar)\\b', ignore_case: true,
    risk: 'low', compliance: 'GDPR', recommended: false,
    message: 'Blocked: the request references a GDPR data-subject request.',
  },
];

// Public catalog: each preset carries its category tag and the persisted-name
// (category-prefixed) so the picker and the save payload stay consistent.
export const GUARDRAIL_PRESETS = RAW_PRESETS.map((p) => ({
  ...p,
  action: 'reject',
  savedName: `[${CATEGORY_TAG[p.category]}] ${p.name}`,
}));

// The fields we actually persist via POST /api/admin/guardrails.
export function presetToPayload(preset, { enabled = true } = {}) {
  return {
    name: preset.savedName,
    pattern: preset.pattern,
    action: 'reject',
    message: preset.message,
    enabled,
    ignore_case: preset.ignore_case,
  };
}

export const RECOMMENDED_PRESET_IDS = GUARDRAIL_PRESETS.filter((p) => p.recommended).map((p) => p.id);
